import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { generateHTML } from '../utils/exportHTML';
import ShortcodeGlossary from './ShortcodeGlossary';
import config from '../config';

function TopBar({ apiStatus, apiEnabled, onSave, onLoad, onClear, grid, currentUid, containerWidth, previewMode, setPreviewMode }) {
    const { user, logout } = useAuth();
    const { subscriptionPlan } = useOrganization();
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportedHtml, setExportedHtml] = useState('');
    const [showUidModal, setShowUidModal] = useState(false);
    const [showGlossary, setShowGlossary] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);

    const handleExport = () => {
        const html = generateHTML(grid, containerWidth, { subscriptionPlan });
        setExportedHtml(html);
        setShowExportModal(true);
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(exportedHtml);
        alert('HTML copié dans le presse-papier !');
    };

    const copySignatureToClipboard = async () => {
        try {
            const html = generateHTML(grid, containerWidth, { subscriptionPlan });

            // Créer un blob HTML
            const htmlBlob = new Blob([html], { type: 'text/html' });
            // Créer une version texte (strip HTML tags)
            const textContent = html.replace(/<[^>]*>/g, '');
            const textBlob = new Blob([textContent], { type: 'text/plain' });

            // Utiliser l'API Clipboard moderne pour copier en multi-format
            const clipboardItem = new ClipboardItem({
                'text/html': htmlBlob,
                'text/plain': textBlob
            });

            await navigator.clipboard.write([clipboardItem]);

            // Afficher un feedback visuel
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            console.error('Erreur lors de la copie:', err);
            alert('Erreur lors de la copie dans le presse-papier. Votre navigateur ne supporte peut-être pas cette fonctionnalité.');
        }
    };

    const downloadHTML = () => {
        const blob = new Blob([exportedHtml], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'signature.html';
        a.click();
        URL.revokeObjectURL(url);
    };

    // CORRECTION #3: Afficher l'UID et l'URL de preview
    const showUidInfo = () => {
        setShowUidModal(true);
    };

    const copyUid = () => {
        if (currentUid) {
            navigator.clipboard.writeText(currentUid);
            alert('UID copié dans le presse-papier !');
        }
    };

    return (
        <>
            <div className="top-bar">
                <h1>📧 Générateur de Signatures Email</h1>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    {/* CORRECTION #2: Affichage du statut API */}
                    {apiEnabled && (
                        <div className="api-status">
                            <span className={`api-status-dot ${apiStatus === 'connected' ? 'connected' : ''}`}></span>
                            <span>API {apiStatus === 'connected' ? 'connectée' : 'déconnectée'}</span>
                        </div>
                    )}

                    <div className="top-bar-actions">
                        {/* Switch Mode Preview */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '10px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
                                <span style={{ color: '#2c3e50' }}>👁️ Preview</span>
                                <div
                                    onClick={() => setPreviewMode(!previewMode)}
                                    style={{
                                        width: '48px',
                                        height: '24px',
                                        background: previewMode ? '#27ae60' : '#95a5a6',
                                        borderRadius: '12px',
                                        position: 'relative',
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer',
                                        boxShadow: previewMode ? '0 2px 8px rgba(39, 174, 96, 0.3)' : '0 2px 4px rgba(0,0,0,0.1)'
                                    }}
                                >
                                    <div style={{
                                        width: '20px',
                                        height: '20px',
                                        background: 'white',
                                        borderRadius: '50%',
                                        position: 'absolute',
                                        top: '2px',
                                        left: previewMode ? '26px' : '2px',
                                        transition: 'all 0.3s ease',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    }} />
                                </div>
                            </label>
                        </div>

                        {/* Bouton Copier signature (visible uniquement en mode preview) */}
                        {previewMode && (
                            <button
                                className={`btn-success ${copySuccess ? 'btn-copied' : ''}`}
                                onClick={copySignatureToClipboard}
                                title="Copier la signature pour Outlook"
                            >
                                {copySuccess ? '✓ Copié !' : '📋 Copier la signature'}
                            </button>
                        )}

                        {/* CORRECTION #1: Boutons toujours visibles si API activée */}
                        {apiEnabled && (
                            <>
                                <button
                                    className="btn-primary"
                                    onClick={onSave}
                                    disabled={apiStatus !== 'connected'}
                                    title={apiStatus !== 'connected' ? 'API déconnectée' : 'Sauvegarder dans le cloud'}
                                >
                                    💾 Sauvegarder
                                </button>
                                <button
                                    className="btn-secondary"
                                    onClick={onLoad}
                                    disabled={apiStatus !== 'connected'}
                                    title={apiStatus !== 'connected' ? 'API déconnectée' : 'Charger depuis le cloud'}
                                >
                                    📥 Charger
                                </button>
                                {/* CORRECTION #3: Bouton pour voir l'UID */}
                                {currentUid && (
                                    <button
                                        className="btn-primary"
                                        onClick={showUidInfo}
                                        title="Voir l'UID et l'URL de preview"
                                    >
                                        🔗 UID / Preview
                                    </button>
                                )}
                            </>
                        )}
                        <button className="btn-success" onClick={handleExport}>📤 Exporter HTML</button>
                        <button className="btn-secondary" onClick={() => setShowGlossary(true)} title="Voir le glossaire des champs Active Directory">
                            📚 Champs AD
                        </button>
                        <button className="btn-danger" onClick={onClear}>🗑️ Effacer</button>

                        {/* Menu utilisateur */}
                        <div style={{ position: 'relative', marginLeft: '10px' }}>
                            <button
                                className="btn-secondary"
                                onClick={() => setShowUserMenu(!showUserMenu)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '8px 16px'
                                }}
                            >
                                👤 {user?.first_name || user?.email || 'Utilisateur'}
                            </button>

                            {showUserMenu && (
                                <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    right: 0,
                                    marginTop: '8px',
                                    background: 'white',
                                    border: '1px solid #ddd',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    minWidth: '200px',
                                    zIndex: 1000
                                }}>
                                    <div style={{
                                        padding: '12px 16px',
                                        borderBottom: '1px solid #eee',
                                        fontSize: '13px',
                                        color: '#666'
                                    }}>
                                        <div style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '4px' }}>
                                            {user?.first_name && user?.last_name
                                                ? `${user.first_name} ${user.last_name}`
                                                : user?.email}
                                        </div>
                                        <div>{user?.email}</div>
                                    </div>
                                    <Link
                                        to="/profile"
                                        style={{
                                            display: 'block',
                                            padding: '12px 16px',
                                            color: '#2c3e50',
                                            textDecoration: 'none',
                                            fontSize: '14px',
                                            transition: 'background 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                                        onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                        onClick={() => setShowUserMenu(false)}
                                    >
                                        ⚙️ Mon Profil
                                    </Link>
                                    <Link
                                        to="/organization/settings"
                                        style={{
                                            display: 'block',
                                            padding: '12px 16px',
                                            color: '#2c3e50',
                                            textDecoration: 'none',
                                            fontSize: '14px',
                                            transition: 'background 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                                        onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                        onClick={() => setShowUserMenu(false)}
                                    >
                                        🏢 Organisation
                                    </Link>
                                    <button
                                        onClick={() => {
                                            setShowUserMenu(false);
                                            logout();
                                        }}
                                        style={{
                                            width: '100%',
                                            padding: '12px 16px',
                                            background: 'transparent',
                                            border: 'none',
                                            borderTop: '1px solid #eee',
                                            textAlign: 'left',
                                            color: '#e74c3c',
                                            fontSize: '14px',
                                            cursor: 'pointer',
                                            transition: 'background 0.2s',
                                            borderRadius: '0 0 8px 8px'
                                        }}
                                        onMouseEnter={(e) => e.target.style.background = '#fee'}
                                        onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                    >
                                        🚪 Déconnexion
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Glossaire des shortcodes */}
            <ShortcodeGlossary
                isOpen={showGlossary}
                onClose={() => setShowGlossary(false)}
            />

            {/* Modal Export HTML */}
            {showExportModal && (
                <div className="modal-overlay" onClick={() => setShowExportModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h2>📤 Export HTML</h2>
                        <textarea value={exportedHtml} readOnly />
                        <div className="modal-actions">
                            <button className="btn-primary" onClick={copyToClipboard}>📋 Copier</button>
                            <button className="btn-success" onClick={downloadHTML}>💾 Télécharger</button>
                            <button className="btn-secondary" onClick={() => setShowExportModal(false)}>Fermer</button>
                        </div>
                    </div>
                </div>
            )}

            {/* CORRECTION #3: Modal UID et Preview */}
            {showUidModal && currentUid && (
                <div className="modal-overlay" onClick={() => setShowUidModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                        <h2>🔗 Informations de Partage</h2>

                        <div className="form-group">
                            <label><strong>UID de la signature:</strong></label>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <input
                                    type="text"
                                    value={currentUid}
                                    readOnly
                                    style={{ flex: 1, fontFamily: 'monospace', fontSize: '14px' }}
                                />
                                <button className="btn-primary" onClick={copyUid}>📋 Copier</button>
                            </div>
                        </div>

                        <div className="form-group">
                            <label><strong>URL de preview HTML:</strong></label>
                            <div style={{
                                background: '#f8f9fa',
                                padding: '12px',
                                borderRadius: '6px',
                                wordBreak: 'break-all',
                                fontSize: '13px',
                                fontFamily: 'monospace'
                            }}>
                                <a
                                    href={`${config.apiUrl}/signature/html/${currentUid}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: '#3498db' }}
                                >
                                    {config.apiUrl}/signature/html/{currentUid}
                                </a>
                            </div>
                            <small style={{ color: '#666', fontSize: '12px', marginTop: '8px', display: 'block' }}>
                                Cliquez sur le lien pour voir la signature en HTML dans un nouvel onglet
                            </small>
                        </div>

                        <div className="form-group">
                            <label><strong>URL JSON (données):</strong></label>
                            <div style={{
                                background: '#f8f9fa',
                                padding: '12px',
                                borderRadius: '6px',
                                wordBreak: 'break-all',
                                fontSize: '13px',
                                fontFamily: 'monospace'
                            }}>
                                <a
                                    href={`${config.apiUrl}/signature/${currentUid}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: '#3498db' }}
                                >
                                    {config.apiUrl}/signature/{currentUid}
                                </a>
                            </div>
                        </div>

                        <div className="modal-actions">
                            <button className="btn-secondary" onClick={() => setShowUidModal(false)}>Fermer</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default TopBar;
